import { useState } from 'react'
import { Link, Route, Routes, useLocation } from 'react-router-dom'

import { BookingPage } from '@/features/BookingPage'
import { BookingStatusPage } from '@/features/BookingStatusPage'
import { CatalogPage } from '@/features/CatalogPage'
import { PropertyPage } from '@/features/PropertyPage'
import type { Search } from '@/features/SearchBar'
import * as d from '@/lib/dates'

const SEARCH_KEY = 'agira.guest.search'

/** Если задан один объект, сайт работает как сайт этого отеля: на корне сразу
 *  его карточка, каталога нет. Один и тот же код обслуживает и витрину
 *  платформы, и отдельный сайт — отличается только переменной сборки. */
const ONLY = import.meta.env.VITE_PROPERTY_SLUG ?? ''

function initialSearch(): Search {
  try {
    const saved = localStorage.getItem(SEARCH_KEY)
    if (saved) {
      const parsed = JSON.parse(saved) as Search
      // Даты из прошлого визита могли протухнуть — тогда начинаем заново.
      if (parsed.checkIn >= d.today()) return parsed
    }
  } catch {
    /* испорченное хранилище не должно ломать вход на сайт */
  }
  const checkIn = d.addDays(d.today(), 1)
  return { checkIn, checkOut: d.addDays(checkIn, 2), adults: 2, children: 0 }
}

export function App() {
  const [search, setSearch] = useState<Search>(initialSearch)
  const location = useLocation()

  function update(next: Search) {
    setSearch(next)
    try {
      localStorage.setItem(SEARCH_KEY, JSON.stringify(next))
    } catch {
      /* приватный режим — просто не запоминаем */
    }
  }

  return (
    <div className="shell">
      <header className="topbar">
        <Link className="brand display" to="/">
          {import.meta.env.VITE_SITE_NAME ?? 'Агира'}
        </Link>
        <nav className="topnav">
          <Link
            className={location.pathname === '/booking' ? 'topnav__link is-active' : 'topnav__link'}
            to="/booking"
          >
            Моя бронь
          </Link>
        </nav>
      </header>

      <main>
        <Routes>
          <Route
            path="/"
            element={
              ONLY ? (
                <PropertyPage search={search} onSearch={update} slug={ONLY} />
              ) : (
                <CatalogPage search={search} onSearch={update} />
              )
            }
          />
          <Route
            path="/hotel/:slug"
            element={<PropertyPage search={search} onSearch={update} />}
          />
          <Route path="/hotel/:slug/book" element={<BookingPage />} />
          <Route path="/booking" element={<BookingStatusPage />} />
          <Route
            path="*"
            element={
              <section className="section empty">
                <p className="empty__title">Страница не найдена</p>
                <Link className="button" to="/">
                  На главную
                </Link>
              </section>
            }
          />
        </Routes>
      </main>

      <footer className="footer">
        <p className="muted">
          Прямое бронирование без комиссии посредников. Оплата при заселении.
        </p>
      </footer>
    </div>
  )
}
